import { useState, useMemo } from "react";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

// At 25 °C, pH + pOH = 14 (Kw = 1.0e-14). All four quantities are
// interconvertible from any one. This is exact textbook chemistry.
function solve(kind, x) {
  if (!isFinite(x)) return null;
  let pH;
  if (kind === "pH") pH = x;
  else if (kind === "pOH") pH = 14 - x;
  else if (kind === "H") { if (x <= 0) return null; pH = -Math.log10(x); }
  else if (kind === "OH") { if (x <= 0) return null; pH = 14 - (-Math.log10(x)); }
  const pOH = 14 - pH;
  const H = Math.pow(10, -pH);
  const OH = Math.pow(10, -pOH);
  return { pH, pOH, H, OH };
}

const fmtConc = (x) => (x >= 1e-4 && x < 1e4) ? x.toPrecision(4) : x.toExponential(3);

export default function PhCalculator() {
  const [kind, setKind] = useState("pH");
  const [val, setVal] = useState("");

  const r = useMemo(() => {
    const v = parseFloat(val);
    if (val === "" || !isFinite(v)) return null;
    return solve(kind, v);
  }, [kind, val]);

  const nature = r ? (r.pH < 6.99 ? ["Acidic", "#F87171"] : r.pH > 7.01 ? ["Basic", "#60A5FA"] : ["Neutral", EM_LIGHT]) : null;
  const inStyle = { width: "100%", padding: "14px 16px", borderRadius: 10, border: `1px solid ${rgba(EM,0.22)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 17, fontFamily: "inherit", outline: "none", boxSizing: "border-box", textAlign: "center" };
  const tabs = [["pH","pH"],["pOH","pOH"],["H","[H⁺] M"],["OH","[OH⁻] M"]];

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>pH Calculator</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 520, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 24px" }}>
          Enter any one of pH, pOH, [H⁺], or [OH⁻] and get the other three.
          Assumes 25 °C (Kw = 1.0 × 10⁻¹⁴, so pH + pOH = 14).
        </p>

        <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>
          {tabs.map(([k, lbl]) => (
            <button key={k} onClick={() => setKind(k)} style={{ flex: 1, padding: "10px 4px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: kind === k ? rgba(EM,0.12) : "rgba(255,255,255,0.02)", border: kind === k ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: kind === k ? EM_LIGHT : "#6B7280" }}>{lbl}</button>
          ))}
        </div>
        <input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Enter ${tabs.find(t=>t[0]===kind)[1]}`} style={inStyle} />

        {r && (
          <>
            <div style={{ textAlign: "center", margin: "26px 0 20px" }}>
              <span style={{ display: "inline-block", padding: "6px 18px", borderRadius: 20, background: rgba(nature[1].replace("#","#"), 0.12), border: `1px solid ${nature[1]}55`, color: nature[1], fontSize: 14, fontWeight: 700, letterSpacing: "0.5px" }}>{nature[0]}</span>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {[["pH", r.pH.toFixed(2)], ["pOH", r.pOH.toFixed(2)], ["[H⁺]", fmtConc(r.H) + " M"], ["[OH⁻]", fmtConc(r.OH) + " M"]].map(([lbl, v]) => (
                <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{lbl}</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: EM_LIGHT }}>{v}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
